import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, LayoutTemplate, Plus, Trash2, Users, ListChecks, HelpCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";

const HERO_FIELDS = [
  ["eyebrow", "Eyebrow / badge line", "input"],
  ["title_lead", "Headline (lead text)", "input"],
  ["title_highlight", "Headline (highlighted word)", "input"],
  ["subtitle", "Sub-headline paragraph", "textarea"],
  ["cta_primary_label", "Primary button label", "input"],
  ["cta_secondary_label", "Secondary button label", "input"],
  ["image_url", "Hero image URL", "input"],
  ["card_title", "Floating card title", "input"],
  ["card_text", "Floating card text", "textarea"],
];

const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-border bg-white p-6">
    <div className="mb-4 flex items-center gap-2"><Icon className="h-5 w-5 text-gold" /><h3 className="font-serif text-lg text-navy">{title}</h3></div>
    {children}
  </div>
);

export default function WebsiteContent() {
  const [content, setContent] = useState(null);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", role: "", bio: "", photo_url: "", linkedin: "", email: "" });

  const loadTeam = () => api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  useEffect(() => {
    api.get("/site-content").then(({ data }) => setContent(data)).catch(() => toast.error("Failed to load content"));
    loadTeam();
  }, []);

  const setHero = (k, v) => setContent((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));

  const saveContent = async () => {
    setSaving(true);
    try {
      const hero = { ...content.hero, badges: Array.isArray(content.hero.badges) ? content.hero.badges.join(", ") : content.hero.badges };
      const { data } = await api.put("/admin/content", { hero, core_values: content.core_values || [], faqs: content.faqs || [] });
      setContent(data);
      toast.success("Website content updated — live on the public site.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  // Core values + FAQs list editing
  const updList = (key, i, field, val) => setContent((c) => { const arr = [...(c[key] || [])]; arr[i] = { ...arr[i], [field]: val }; return { ...c, [key]: arr }; });
  const addRow = (key, tpl) => setContent((c) => ({ ...c, [key]: [...(c[key] || []), tpl] }));
  const delRow = (key, i) => setContent((c) => ({ ...c, [key]: (c[key] || []).filter((_, idx) => idx !== i) }));

  // Team CRUD (persists immediately)
  const addMember = async () => {
    if (!newMember.name.trim()) { toast.error("Name is required"); return; }
    try { await api.post("/admin/team", newMember); toast.success("Team member added"); setNewMember({ name: "", role: "", bio: "", photo_url: "", linkedin: "", email: "" }); loadTeam(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveMember = async (m) => { try { await api.put(`/admin/team/${m.id}`, m); toast.success("Saved"); loadTeam(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const delMember = async (m) => { if (!window.confirm(`Remove ${m.name}?`)) return; try { await api.delete(`/admin/team/${m.id}`); toast.success("Removed"); loadTeam(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const setMember = (i, field, val) => setTeam((t) => { const a = [...t]; a[i] = { ...a[i], [field]: val }; return a; });

  if (!content) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;
  const hero = content.hero || {};
  const badgesStr = Array.isArray(hero.badges) ? hero.badges.join(", ") : (hero.badges || "");

  return (
    <div className="space-y-6" data-testid="content-panel">
      <div className="flex items-center gap-2">
        <LayoutTemplate className="h-5 w-5 text-gold" />
        <div>
          <h2 className="font-serif text-2xl text-navy">Website Content (CMS)</h2>
          <p className="text-sm text-muted-foreground">Edit homepage hero, Group Executive team, core values and FAQs — no code. Brand colours, logo, contact &amp; social live under Brand &amp; Social.</p>
        </div>
      </div>

      {/* HERO */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2"><Card title="Homepage Hero" icon={LayoutTemplate}>
          <div className="grid gap-4 sm:grid-cols-2">
            {HERO_FIELDS.map(([k, label, type]) => (
              <div key={k} className={type === "textarea" || k === "image_url" ? "sm:col-span-2" : ""}>
                <label className="mb-1 block text-xs font-semibold text-charcoal/70">{label}</label>
                {type === "textarea"
                  ? <textarea rows={3} className={inputCls} value={hero[k] || ""} onChange={(e) => setHero(k, e.target.value)} data-testid={`content-hero-${k}`} />
                  : <input className={inputCls} value={hero[k] || ""} onChange={(e) => setHero(k, e.target.value)} data-testid={`content-hero-${k}`} />}
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-charcoal/70">Trust badges (comma separated)</label>
              <input className={inputCls} value={badgesStr} onChange={(e) => setHero("badges", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="content-hero-badges" />
            </div>
          </div>
        </Card></div>
        <Card title="Live Preview" icon={LayoutTemplate}>
          <div className="overflow-hidden rounded-xl bg-navy p-5 text-white">
            <span className="inline-block rounded-full border border-white/15 px-3 py-1 text-[10px] text-gold-light">{hero.eyebrow}</span>
            <p className="mt-3 font-serif text-2xl leading-tight">{hero.title_lead} <span className="text-gold">{hero.title_highlight}</span></p>
            <p className="mt-2 text-xs text-white/70 line-clamp-4">{hero.subtitle}</p>
          </div>
          {hero.image_url && <img src={hero.image_url} alt="hero" className="mt-3 h-32 w-full rounded-lg object-cover" data-testid="content-hero-preview-img" />}
        </Card>
      </div>

      {/* CORE VALUES */}
      <Card title="Our Core Values" icon={ListChecks}>
        <div className="space-y-3" data-testid="content-values">
          {(content.core_values || []).map((v, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]" data-testid={`value-row-${i}`}>
              <input className={inputCls} placeholder="Title" value={v.title} onChange={(e) => updList("core_values", i, "title", e.target.value)} />
              <input className={inputCls} placeholder="Description" value={v.description} onChange={(e) => updList("core_values", i, "description", e.target.value)} />
              <button onClick={() => delRow("core_values", i)} className="rounded-md border border-destructive/40 px-3 text-destructive hover:bg-destructive/5"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => addRow("core_values", { title: "", description: "" })} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold" data-testid="value-add"><Plus className="h-4 w-4" /> Add value</button>
        </div>
      </Card>

      {/* FAQs */}
      <Card title="Frequently Asked Questions" icon={HelpCircle}>
        <div className="space-y-3" data-testid="content-faqs">
          {(content.faqs || []).map((f, i) => (
            <div key={i} className="rounded-lg border border-border p-3" data-testid={`faq-row-${i}`}>
              <input className={`${inputCls} mb-2`} placeholder="Question" value={f.q} onChange={(e) => updList("faqs", i, "q", e.target.value)} />
              <textarea rows={2} className={inputCls} placeholder="Answer" value={f.a} onChange={(e) => updList("faqs", i, "a", e.target.value)} />
              <button onClick={() => delRow("faqs", i)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-destructive hover:underline"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
            </div>
          ))}
          <button onClick={() => addRow("faqs", { q: "", a: "" })} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold" data-testid="faq-add"><Plus className="h-4 w-4" /> Add FAQ</button>
        </div>
      </Card>

      <button onClick={saveContent} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="content-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; Publish (Hero, Values, FAQs)
      </button>

      {/* GROUP EXECUTIVE / TEAM */}
      <Card title="Group Executive (Team)" icon={Users}>
        <p className="mb-4 text-sm text-muted-foreground">Add, edit or remove leadership team members shown on the public site. Changes save immediately.</p>
        <div className="space-y-4" data-testid="content-team">
          {team.map((m, i) => (
            <div key={m.id} className="rounded-xl border border-border p-4" data-testid={`team-row-${m.id}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <input className={inputCls} placeholder="Name" value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} />
                <input className={inputCls} placeholder="Role / Title" value={m.role} onChange={(e) => setMember(i, "role", e.target.value)} />
                <input className={`${inputCls} sm:col-span-2`} placeholder="Photo URL" value={m.photo_url} onChange={(e) => setMember(i, "photo_url", e.target.value)} />
                <textarea rows={2} className={`${inputCls} sm:col-span-2`} placeholder="Bio" value={m.bio} onChange={(e) => setMember(i, "bio", e.target.value)} />
                <input className={inputCls} placeholder="LinkedIn URL" value={m.linkedin} onChange={(e) => setMember(i, "linkedin", e.target.value)} />
                <input className={inputCls} placeholder="Email" value={m.email} onChange={(e) => setMember(i, "email", e.target.value)} />
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => saveMember(m)} className="rounded-lg bg-navy px-4 py-1.5 text-xs font-semibold text-white hover:bg-navy-light" data-testid={`team-save-${m.id}`}>Save</button>
                <button onClick={() => delMember(m)} className="rounded-lg border border-destructive/40 px-4 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/5" data-testid={`team-del-${m.id}`}>Remove</button>
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-dashed border-gold/50 bg-gold/[0.04] p-4">
            <p className="mb-3 text-sm font-semibold text-navy">Add new member</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inputCls} placeholder="Name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} data-testid="team-new-name" />
              <input className={inputCls} placeholder="Role / Title" value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} data-testid="team-new-role" />
              <input className={`${inputCls} sm:col-span-2`} placeholder="Photo URL" value={newMember.photo_url} onChange={(e) => setNewMember({ ...newMember, photo_url: e.target.value })} data-testid="team-new-photo" />
              <textarea rows={2} className={`${inputCls} sm:col-span-2`} placeholder="Bio" value={newMember.bio} onChange={(e) => setNewMember({ ...newMember, bio: e.target.value })} data-testid="team-new-bio" />
              <input className={inputCls} placeholder="LinkedIn URL" value={newMember.linkedin} onChange={(e) => setNewMember({ ...newMember, linkedin: e.target.value })} />
              <input className={inputCls} placeholder="Email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} />
            </div>
            <button onClick={addMember} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="team-add-btn"><Plus className="h-4 w-4" /> Add Member</button>
          </div>
        </div>
      </Card>
    </div>
  );
}
